import { getFlag } from '@mahawi/eshop-common/dist/src/get-flag';
import {
  EEventContextAction,
  EEventType,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { getHead } from '../../head';
import OrderDetailView, {
  type IOrderDetailViewAddress,
  type IOrderDetailViewProduct,
} from '../components/order-detail-view';
import { type ICountryState } from '../reducers/country/types';
import { eventSend } from '../reducers/event/reducer';
import { type ILanguageState } from '../reducers/language/types';
import { paymentRetry } from '../reducers/order/reducer';
import { type IOrderState } from '../reducers/order/types';
import { setActiveCategory } from '../reducers/products/reducer';

function OrderContainer({
  Order,
  Country,
  Language,
  dispatch,
}: {
  Order: IOrderState;
  Country: ICountryState;
  Language: ILanguageState;
  dispatch: Dispatch;
}): React.JSX.Element {
  const { t } = useTranslation();
  const {
    languageType: { code: languageCode },
  } = Language;
  const lng = languageCode.toLowerCase();
  const [searchParams] = useSearchParams();
  const isPaymentUnavailable: boolean =
    searchParams.get('payment') === 'unavailable';

  useEffect((): void => {
    if (Order.redirectLink) {
      window.location.href = Order.redirectLink;
    }
  }, [Order.redirectLink]);

  const handlePayNow = useCallback((): void => {
    dispatch(paymentRetry({ orderUUID: Order.uuid }));
  }, [dispatch, Order.uuid]);

  useEffect((): (() => void) => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_ORDER,
        action: EEventContextAction.ENTER,
        context: { order: { uuid: Order.uuid } },
      }),
    );
    dispatch(setActiveCategory({ category: { uuid: '' } }));

    return (): void => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_ORDER,
          action: EEventContextAction.LEAVE,
          context: { order: { uuid: Order.uuid } },
        }),
      );
    };
  }, [dispatch]);

  useEffect((): void => {
    const { title } = getHead(t, t('navigation.title.order'));
    document.title = title;
  }, [t]);

  const makeCountryLabel = (isoCode: string): React.JSX.Element | null => {
    if (!isoCode) {
      return null;
    }
    const flag = getFlag({ countryTypeIsoCode: isoCode });
    const country = Country.countries.find(
      (c) => c.isoCode === isoCode || c.isoCode2 === isoCode,
    );
    return (
      <>
        <span className="flag-emoji">{flag}</span> {country?.name ?? isoCode}
      </>
    );
  };

  const mapAddress = (addr: {
    firstName: string;
    lastName: string;
    street: string;
    city: string;
    postalCode: string;
    countryIsoCode: string;
  }): IOrderDetailViewAddress => ({
    name: addr.firstName,
    surname: addr.lastName,
    street: addr.street,
    city: addr.city,
    zip: addr.postalCode,
    countryLabel: makeCountryLabel(addr.countryIsoCode),
  });

  const priceWithTax = Order.currencyPrice.pricesWithTax[0];
  const totalPrice = Order.companyHasPricesWithoutTax
    ? Order.currencyPrice.priceWithoutTax
    : (priceWithTax?.price ?? 0);

  const deliveryFee = Order.companyHasPricesWithoutTax
    ? Order.orderDelivery.feeWithoutTax
    : Order.orderDelivery.fee;

  const products: IOrderDetailViewProduct[] = (Order.products ?? []).map(
    (product) => {
      const productPriceWithTax = product.currencyPrice.pricesWithTax[0];
      const pricePerUnit = Order.companyHasPricesWithoutTax
        ? product.currencyPrice.priceWithoutTax
        : (productPriceWithTax?.price ?? 0);
      const name =
        product.names.find((n) => n.code === languageCode)?.value ?? '';
      const slug = product.slugs.find((s) => s.code === languageCode)?.value;
      const link = slug
        ? `/${lng}/p/${slug}`
        : `/${lng}/product/${product.uuid}`;
      return {
        uuid: product.uuid,
        name,
        link,
        count: product.count,
        pricePerUnit,
        lineTotal: pricePerUnit * product.count,
      };
    },
  );

  return (
    <>
      {isPaymentUnavailable && !Order.paymentRetryError && (
        <p
          className="account-order-detail__pay-error account-order-detail__pay-error--notice"
          role="alert"
        >
          {t('account.orderDetail.paymentUnavailable')}
        </p>
      )}
      <OrderDetailView
        uuid={Order.uuid}
        dispatch={dispatch}
        orderStatusCode={Order.orderStatusCode}
        currencyIsoCode={Order.currencyPrice.isoCode}
        companyHasPricesWithoutTax={Order.companyHasPricesWithoutTax}
        totalPrice={totalPrice}
        deliveryFee={deliveryFee}
        products={products}
        billingAddress={
          Order.billingAddress ? mapAddress(Order.billingAddress) : undefined
        }
        deliveryAddress={
          Order.deliveryAddress ? mapAddress(Order.deliveryAddress) : null
        }
        paymentMethodName={Order.paymentMethodName}
        deliveryMethodName={Order.deliveryMethodName}
        onPayNow={handlePayNow}
        isRetryingPayment={Order.isRetryingPayment}
        paymentRetryError={Order.paymentRetryError}
      />
    </>
  );
}

const mapStateToProps = (state: {
  Country: ICountryState;
  Language: ILanguageState;
  Order: IOrderState;
}) => ({
  Country: state.Country,
  Language: state.Language,
  Order: state.Order,
});

export default connect(mapStateToProps)(OrderContainer);
